import React, { useEffect, useReducer, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { MdAttachMoney } from 'react-icons/md';
import { FiCalendar, FiLayers, FiRepeat } from 'react-icons/fi';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from '../../toasts';
import DetailPageLoader from '../../components/DetailPageLoader/DetailPageLoader';
import MessageBox from '../../components/MessageBox';
import BillService from '../../services/bill.service';
import { getError, parsePeriodicity, showCurrency } from '../../utils';
import HistoryModal from '../../components/HistoryModal';
import ConfirmationModal from '../../components/ConfirmationModal/ConfirmationModal';
import SingleObjectDocumentManager from '../../components/SingleObjectDocumentManager/SingleObjectDocumentManager';
import ConfirmPayment from './ConfirmPayment';
import BillInfo from './BillInfo';
import { useLabels } from '../../hooks/useLabels';
import { EntityList, DetailHeader, TabNav, Button } from '../../components/EntityList';

const reducer = (state, action) => {
	switch (action.type) {
	case 'FETCH_REQUEST':
		return { ...state, loading: true };
	case 'FETCH_SUCCESS':
		return { ...state, bill: action.payload, loading: false };
	case 'FETCH_FAIL':
		return { ...state, loading: false, error: action.payload };
	default:
		return state;
	}
};

const TAB_SLUGS = ['info', 'documents'];

function formatDate(date) {
	return date ? new Date(date).toLocaleDateString('es-UY') : '';
}

function formatMoney(currency, amount) {
	return `${showCurrency(currency) || ''} ${Number(amount || 0).toLocaleString('es-UY')}`.trim();
}

function BillDetail() {
	const { t } = useLabels();
	const params = useParams();
	const navigate = useNavigate();

	const { id, tab } = params;
	const [{ bill, loading, error }, dispatch] = useReducer(reducer, {
		bill: {},
		loading: true,
		error: '',
	});

	const [showHistoryLog, setShowHistoryLog] = useState(false);
	const [showConfirmPaymentModal, setShowConfirmPaymentModal] = useState(false);
	const [showConfirmationModal, setShowConfirmationModal] = useState(false);

	const openEditorPage = () => navigate(`/AdminScreen/bills/editBill/${id}`, {
		state: { returnTo: `/AdminScreen/bills/bill/${id}/info` },
	});

	const fetchData = async () => {
		dispatch({ type: 'FETCH_REQUEST' });
		try {
			const result = await BillService.getDetails(id);
			dispatch({ type: 'FETCH_SUCCESS', payload: result.data });
		} catch (error) {
			dispatch({ type: 'FETCH_FAIL', payload: getError(error) });
			toast.error(t('msg.listLoadFailed'));
		}
	};

	useEffect(() => {
		fetchData();
	}, [id]);

	const togglePeriodicity = async () => {
		try {
			if (bill.billTemplate.active) {
				await BillService.deactivateBillTemplate(bill.billTemplate._id);
				toast.success(t('msg.billPeriodicityStopped'));
			} else {
				await BillService.activate(bill.billTemplate._id);
				toast.success(t('msg.billPeriodicityActivated'));
			}
			fetchData();
		} catch (error) {
			toast.error(getError(error));
		}
	};

	const currentTab = TAB_SLUGS.includes(tab) ? tab : 'info';

	useEffect(() => {
		if (!loading && tab !== currentTab) {
			navigate(`/AdminScreen/bills/bill/${id}/${currentTab}`, { replace: true });
		}
	}, [loading, tab, currentTab, id]);

	const tabs = [
		{ id: 'info', label: t('title.details') },
		{ id: 'documents', label: t('title.documents') },
	];

	const isPaid = Boolean(bill.paid || bill.confirmed);
	const isPeriodic = bill.type === 'Periodico';

	const subline = [
		bill.amount && {
			label: t('field.amount'),
			value: <span className='tw:font-mono tw:font-medium tw:text-cat-4'>{formatMoney(bill.asset?.currency, bill.amount)}</span>,
		},
		bill.expirationDate && {
			label: t('field.expirationDate'),
			icon: <FiCalendar />,
			value: formatDate(bill.expirationDate),
		},
		bill.asset && {
			label: t('field.linkedAssetLower'),
			icon: <FiLayers />,
			value: bill.asset.customId || bill.asset.name,
			strong: true,
		},
		isPeriodic && bill.periodicity && {
			label: t('field.periodicity'),
			icon: <FiRepeat />,
			value: parsePeriodicity(bill.periodicity),
		},
	].filter(Boolean);

	return loading ? (
		<EntityList>
			<DetailPageLoader entityLabel={t('entity.bill')} />
		</EntityList>
	) : error ? (
		<MessageBox variant='danger'>{error}</MessageBox>
	) : (
		<EntityList>
			<Helmet>
				<title>{`${t('entity.bill')}: ${bill.customId || ''}`}</title>
			</Helmet>

			<div className='tw:mb-4 tw:flex tw:items-center tw:gap-1.5 tw:text-[12.5px] tw:text-ink-3'>
				<span className='tw:cursor-pointer tw:hover:text-ink-1' onClick={() => navigate('/AdminScreen/bills')}>
					{t('entity.bill.plural')}
				</span>
				<span>/</span>
				<span className='tw:text-ink-1'>{bill.customId}</span>
			</div>

			<DetailHeader
				name={bill.name || ''}
				kicker={`${t('entity.bill')} · ${bill.customId || ''}`}
				statusLabel={isPaid ? t('field.paidFull') : t('field.pendingPayment')}
				statusTone={isPaid ? 'ok' : 'warn'}
				subline={subline}
				avatar={
					<span className='tw:flex tw:h-[72px] tw:w-[72px] tw:shrink-0 tw:items-center tw:justify-center tw:rounded-[14px] tw:bg-cat-4 tw:text-white tw:shadow-sm'>
						<MdAttachMoney size={28} />
					</span>
				}
				actions={
					<>
						<Button onClick={() => setShowHistoryLog(true)}>{t('title.changeHistory')}</Button>
						{!isPaid && (
							<Button onClick={() => setShowConfirmPaymentModal(true)}>{t('action.confirmPayment')}</Button>
						)}
						{isPeriodic && bill.billTemplate && (
							<Button onClick={() => setShowConfirmationModal(true)}>
								{bill.billTemplate.active ? t('action.stopPeriodicity') : t('action.activatePeriodicity')}
							</Button>
						)}
						<Button variant='primary' onClick={openEditorPage}>
							{t('action.edit')}
						</Button>
					</>
				}
			/>

			<div className='tw:mt-5'>
				<TabNav
					tabs={tabs}
					current={currentTab}
					onChange={(nextTab) => navigate(`/AdminScreen/bills/bill/${id}/${nextTab}`)}
				/>
			</div>

			<div className='tw:mt-6'>
				{currentTab === 'info' && <BillInfo bill={bill} />}
				{currentTab === 'documents' && (
					<SingleObjectDocumentManager objectId={id} objectType='bill' documentType='comprobante' />
				)}
			</div>

			<HistoryModal model='bill' show={showHistoryLog} handleClose={() => setShowHistoryLog(false)} id={id} isHistory={true} />

			{bill.billTemplate && (
				<ConfirmationModal
					show={showConfirmationModal}
					handleClose={() => setShowConfirmationModal(false)}
					handleConfirm={togglePeriodicity}
					title={bill.billTemplate.active ? t('action.stopPeriodicity') : t('action.activatePeriodicity')}
					description={bill.billTemplate.active ? t('msg.confirmStopBillPeriodicity') : t('msg.confirmActivateBillPeriodicity')}
				/>
			)}

			{showConfirmPaymentModal && (
				<ConfirmPayment
					id={bill._id}
					onClose={() => setShowConfirmPaymentModal(false)}
					onSaved={fetchData}
				/>
			)}
		</EntityList>
	);
}

export default BillDetail;
